import React from 'react'

import { FieldValues, Path, useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

interface ToggleAllButtonProps<TFieldValues extends FieldValues, T> {
  name: Path<TFieldValues>
  availableValues: T[]
  className?: string
}

export const ToggleAllButton = <TFieldValues extends FieldValues, T>({
  name,
  availableValues,
  className,
}: ToggleAllButtonProps<TFieldValues, T>) => {
  const { setValue, watch } = useFormContext<TFieldValues>()

  // watch the field to re-render on changes
  const selectedValues = watch(name) as T[] | null

  const allSelected =
    selectedValues &&
    availableValues.every((value) =>
      selectedValues.some((selected) => selected === value),
    )

  const handleToggle = () => {
    if (allSelected) {
      // Deselect all
      setValue(name, [] as any)
    } else {
      // Select all
      setValue(name, availableValues as any)
    }
  }

  if (availableValues.length === 0) {
    return null
  }

  return (
    <button
      type="button"
      // need to set the height to 14px to prevent layout shift of the input and label
      // (i think the icon causes the shift)
      className={twMerge(
        'inline-flex items-center gap-x-[1px] h-[14px] leading-none text-sm text-blue-600 dark:text-blue-400 hover:opacity-70',
        className,
      )}
      onClick={handleToggle}
    >
      {allSelected ? 'Deselect All' : 'Select All'}
    </button>
  )
}

interface ClearAllButtonProps {
  name: Path<FieldValues>
}

export const ClearAllButton: React.FC<ClearAllButtonProps> = ({ name }) => {
  const { setValue } = useFormContext()

  const handleClearFiles = () => {
    setValue(name, [] as any)
  }

  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 h-[14px] leading-none text-sm text-red-600 dark:text-red-500 hover:opacity-70"
      onClick={handleClearFiles}
    >
      <span className="text-sm">Clear</span>
    </button>
  )
}
